<template>
	<q-dialog v-model="store.open">
		<div class="table-terminal">
			<div class="corner corner-tl"></div>
			<div class="corner corner-tr"></div>
			<div class="corner corner-bl"></div>
			<div class="corner corner-br"></div>
			<div class="table-frame">
				<div class="table-term-header">
					<span>DATA READOUT // {{ store.highlight ? store.highlight.toUpperCase() : 'FULL TABLE' }}</span>
					<div class="close-btn-wrap">
						<ScifiButton text="CLOSE" buttonType="negative" @click="store.close()" minWidth="70px" />
					</div>
				</div>

				<div class="table-body">
					<table class="pda-table">
						<thead>
							<tr>
								<th
									v-for="(h, hi) in store.headers"
									:key="h"
									:class="{ highlighted: isColumnHighlighted(hi) }"
								>{{ h }}</th>
							</tr>
						</thead>
						<tbody>
							<tr
								v-for="row in store.rows"
								:key="row.label"
								:class="{ 'highlighted-row': isRowHighlighted(row.label) }"
							>
								<td class="row-label">{{ row.label }}</td>
								<td
									v-for="(val, i) in row.values"
									:key="i"
									:class="{ highlighted: isColumnHighlighted(i + 1) }"
								>{{ val }}</td>
							</tr>
						</tbody>
					</table>
				</div>
			</div>
		</div>
	</q-dialog>
</template>

<script setup lang="ts">
import { useTableDialogStore } from '~/stores/tableDialog';

const store = useTableDialogStore();

const highlightColIndex = computed(() => {
	if (!store.highlight || store.arrangement === 'byRow') return -1;
	return store.headers.findIndex(h => h.toLowerCase() === store.highlight.toLowerCase());
});

function isColumnHighlighted(colIndex: number): boolean {
	return store.arrangement === 'byColumn' && colIndex === highlightColIndex.value;
}

function isRowHighlighted(rowLabel: string): boolean {
	return store.arrangement === 'byRow' && rowLabel.toLowerCase() === store.highlight.toLowerCase();
}
</script>

<style scoped lang="scss">
.table-terminal {
	width: fit-content;
	min-width: 50vw;
	max-width: 95vw;
	max-height: 85vh;
	background: #060d10;
	border: 1px solid #00ffea40;
	border-radius: 2px;
	box-shadow: 0 0 40px #00ffea10, 0 0 80px #00000080;
	overflow: hidden;
	position: relative;

	// Scanline overlay
	&::before {
		content: '';
		position: absolute;
		top: 0;
		left: 0;
		right: 0;
		bottom: 0;
		background: repeating-linear-gradient(
			0deg,
			transparent,
			transparent 2px,
			rgba(0, 255, 234, 0.015) 2px,
			rgba(0, 255, 234, 0.015) 4px
		);
		pointer-events: none;
		z-index: 1;
	}

}

.corner {
	position: absolute;
	z-index: 3;
	pointer-events: none;

	&.corner-tl {
		top: -1px;
		left: -1px;
		width: 40px;
		height: 40px;
		border-top: 3px solid #ff6600;
		border-left: 3px solid #ff6600;
		box-shadow: inset 4px 4px 8px #ff660030;
	}

	&.corner-tr {
		top: -1px;
		right: -1px;
		width: 40px;
		height: 40px;
		border-top: 3px solid #ff6600;
		border-right: 3px solid #ff6600;
		box-shadow: inset -4px 4px 8px #ff660030;
	}

	&.corner-bl {
		bottom: -1px;
		left: -1px;
		width: 40px;
		height: 40px;
		border-bottom: 3px solid #00ffea;
		border-left: 3px solid #00ffea;
		box-shadow: inset 4px -4px 8px #00ffea20;
	}

	&.corner-br {
		bottom: -1px;
		right: -1px;
		width: 40px;
		height: 40px;
		border-bottom: 3px solid #00ffea;
		border-right: 3px solid #00ffea;
		box-shadow: inset -4px -4px 8px #00ffea20;
	}
}

.table-frame {
	position: relative;
	z-index: 2;
}

.table-term-header {
	display: flex;
	justify-content: space-between;
	align-items: center;
	padding: 8px 16px;
	background: linear-gradient(180deg, #00ffea08 0%, transparent 100%);
	border-bottom: 1px solid #00ffea20;
	font-family: 'Share Tech Mono', 'Courier New', monospace;
	font-size: 0.8em;
	color: #00ffea80;
	letter-spacing: 2px;
	text-transform: uppercase;

	// Top accent line
	&::before {
		content: '';
		position: absolute;
		top: 0;
		left: 0;
		right: 0;
		height: 2px;
		background: linear-gradient(90deg, #ff660000, #ff660080, #00ffea80, #ff660080, #ff660000);
	}
}

.close-btn-wrap {
	flex-shrink: 0;
}

.table-body {
	padding: 16px 24px;
	overflow: auto;
	max-height: calc(85vh - 50px);
}

.pda-table {
	border-collapse: collapse;
	background: transparent;
	white-space: nowrap;

	th {
		font-family: 'Share Tech Mono', 'Courier New', monospace;
		color: #00ffeacc;
		font-size: 0.8em;
		font-weight: bold;
		text-transform: uppercase;
		letter-spacing: 1px;
		padding: 8px 14px;
		border-bottom: 2px solid #00ffea30;
		text-shadow: 0 0 6px #00ffea30;
		white-space: nowrap;
		text-align: center;
		background: #00ffea06;
	}

	th:first-child {
		text-align: left;
		color: #ff6600cc;
	}

	td {
		font-family: 'Share Tech Mono', 'Courier New', monospace;
		color: #51ff00;
		font-size: 0.9em;
		font-weight: bold;
		padding: 5px 14px;
		border-bottom: 1px solid #00ffea08;
		text-align: center;
	}

	.row-label {
		color: #00ffeaa0;
		font-weight: normal;
		text-align: left;
		white-space: nowrap;
		border-right: 1px solid #00ffea15;
		padding-right: 20px;
	}

	tbody tr:hover td {
		background: #00ffea0a;
	}

	tbody tr:nth-child(even) td {
		background: #00ffea03;
	}

	tbody tr:nth-child(even):hover td {
		background: #00ffea0a;
	}

	// Column highlight
	th.highlighted,
	td.highlighted {
		background: #ff660015 !important;
		box-shadow: inset 0 0 12px #ff660010;
	}

	th.highlighted {
		color: #ff6600 !important;
		border-bottom-color: #ff660060;
	}

	// Row highlight
	tr.highlighted-row td {
		background: #ff660015 !important;
		box-shadow: inset 0 0 12px #ff660010;
	}

	tr.highlighted-row .row-label {
		color: #ff6600 !important;
	}
}
</style>
